import type { TagMeta } from '@affine/core/components/page-list';
import { createLitPortal } from '@blocksuite/affine/components/portal';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { unsafeCSSVarV2 } from '@blocksuite/affine/shared/theme';
import { ShadowlessElement } from '@blocksuite/affine/std';
import { MoreVerticalIcon } from '@blocksuite/icons/lit';
import { flip, offset } from '@floating-ui/dom';
import { computed, type Signal, signal } from '@preact/signals-core';
import { css, html, nothing, type PropertyValues } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { isEqual } from 'lodash-es';

import { filterCanvasSelectionsByCoveredBlockIds } from '../../utils/extract';
import type { MailMessageAttachment } from '../../utils/mail-selection-context';
import type {
  AutomationAttachment,
  CanvasAIChatContextMode,
  CodeSelectionAttachment,
  DatabaseAttachment,
  PdfContextAttachment,
  WorkflowAttachment,
} from '../ai-chat-content/type';
import type { CanonicalMessageContextItem } from './canonical-message-context';
import type { ChatChip, DocChip, DocDisplayConfig } from './type';
import {
  estimateTokenCount,
  getChipKey,
  isAttachmentChip,
  isCollectionChip,
  isDocChip,
  isFileChip,
  isSelectedContextChip,
  isTagChip,
} from './utils';

// 100k tokens limit for the docs context
const MAX_TOKEN_COUNT = 100000;

const MAX_CANDIDATES = 3;

export class ChatPanelChips extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
      min-width: 0;
      background: transparent;
    }

    .ai-chat-panel-chips {
      display: flex;
      position: relative;
      width: 100%;
      min-width: 0;
      gap: 8px;
      align-items: center;
      flex-wrap: wrap;
      padding: 4px 12px;
      background: transparent;
      box-sizing: border-box;

      chat-panel-context-group-chip {
        max-width: 100%;
        min-width: 0;
      }

      .collapse-button,
      .more-candidate-button {
        display: flex;
        flex-shrink: 0;
        flex-grow: 0;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border: 0.5px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
        border-radius: 4px;
        box-sizing: border-box;
        cursor: pointer;
        font-size: 12px;
        color: ${unsafeCSSVarV2('icon/primary')};
      }

      .collapse-button:hover,
      .more-candidate-button:hover {
        background-color: ${unsafeCSSVarV2('layer/background/hoverOverlay')};
      }

      .more-candidate-button {
        border-width: 1px;
        border-style: dashed;
        border-color: ${unsafeCSSVarV2('icon/tertiary')};
        background: ${unsafeCSSVarV2('layer/background/secondary')};
        color: ${unsafeCSSVarV2('icon/secondary')};
      }

      .more-candidate-button svg {
        color: ${unsafeCSSVarV2('icon/secondary')};
      }
    }
  `;

  private _abortController: AbortController | null = null;

  @property({ attribute: false })
  accessor chips!: ChatChip[];

  @property({ attribute: false })
  accessor isCollapsed!: boolean;

  @property({ attribute: false })
  accessor independentMode: boolean | undefined;

  @property({ attribute: false })
  accessor canvasAIChatContextMode: CanvasAIChatContextMode = 'lineage-only';

  @property({ attribute: false })
  accessor addChip!: (chip: ChatChip) => Promise<void>;

  @property({ attribute: false })
  accessor updateChip!: (chip: ChatChip, options: Partial<ChatChip>) => void;

  @property({ attribute: false })
  accessor removeChip!: (chip: ChatChip) => Promise<void>;

  @property({ attribute: false })
  accessor toggleCollapse!: () => void;

  @property({ attribute: false })
  accessor docDisplayConfig!: DocDisplayConfig;

  @property({ attribute: false })
  accessor portalContainer: HTMLElement | null = null;

  @property({ attribute: false })
  accessor contextOnly = false;

  @property({ attribute: false })
  accessor databaseAttachments: DatabaseAttachment[] = [];

  @property({ attribute: false })
  accessor removeDatabaseAttachment!: (database: DatabaseAttachment) => void;

  @property({ attribute: false })
  accessor automationAttachments: AutomationAttachment[] = [];

  @property({ attribute: false })
  accessor removeAutomationAttachment!: (
    automation: AutomationAttachment
  ) => void;

  // sl-dpq2.5: Workflow attachments
  @property({ attribute: false })
  accessor workflowAttachments: WorkflowAttachment[] = [];

  @property({ attribute: false })
  accessor removeWorkflowAttachment:
    | ((workflow: WorkflowAttachment) => void)
    | undefined;

  @property({ attribute: false })
  accessor codeSelections: CodeSelectionAttachment[] = [];

  @property({ attribute: false })
  accessor removeCodeSelection:
    | ((selection: CodeSelectionAttachment) => void)
    | undefined;

  @property({ attribute: false })
  accessor mailAttachments: MailMessageAttachment[] = [];

  @property({ attribute: false })
  accessor removeMailAttachment:
    | ((mail: MailMessageAttachment) => void)
    | undefined;

  // sl-w252: PDF context attachments (Weg B — Canvas Selection)
  @property({ attribute: false })
  accessor pdfContextAttachments: PdfContextAttachment[] = [];

  @property({ attribute: false })
  accessor genericContextItems: CanonicalMessageContextItem[] = [];

  @property({ attribute: false })
  accessor removePdfContextAttachment:
    | ((pdf: PdfContextAttachment) => void)
    | undefined;

  @property({ attribute: false })
  accessor removeCanvasSelectionItem:
    | ((chip: ChatChip, itemId: string) => void)
    | undefined;

  @property({ attribute: false })
  accessor removeCanvasSelectionItems:
    | ((chip: ChatChip, itemIds: string[]) => void)
    | undefined;

  @property({ attribute: 'data-testid', reflect: true })
  accessor testId = 'chat-panel-chips';

  @query('.more-candidate-button')
  accessor moreCandidateButton!: HTMLDivElement;

  @state()
  accessor referenceDocs: Signal<
    Array<{
      docId: string;
      title: string;
    }>
  > = signal([]);

  private _tags: Signal<TagMeta[]> = signal([]);

  private _collections: Signal<{ id: string; name: string }[]> = signal([]);

  private _cleanup: (() => void) | null = null;

  private _docIds: string[] = [];

  override render() {
    const candidates: DocChip[] = this.referenceDocs.value.map(doc => ({
      docId: doc.docId,
      state: 'candidate',
    }));
    const moreCandidates = candidates.length > MAX_CANDIDATES;
    const coveredContextBlockIds = [
      ...this.databaseAttachments.map(attachment => attachment.blockId),
      ...this.automationAttachments.map(attachment => attachment.blockId),
      ...this.pdfContextAttachments.map(attachment => attachment.blockId),
    ];
    const selectedContextChips = this.chips
      .filter(isSelectedContextChip)
      .map(chip => ({
        ...chip,
        canvasSelections: filterCanvasSelectionsByCoveredBlockIds(
          chip.canvasSelections ?? [],
          coveredContextBlockIds,
          this.canvasAIChatContextMode
        ),
      }))
      .filter(chip => (chip.canvasSelections ?? []).length > 0);
    const baseNonContextChips = this.chips.filter(
      chip => !isSelectedContextChip(chip)
    );
    const docContextChips = this.contextOnly
      ? this.chips
          .filter(isDocChip)
          .filter(chip => chip.state !== 'candidate')
          .map(chip => ({
            chip,
            label: this.docDisplayConfig.getTitle(chip.docId) || chip.docId,
            meta: 'document',
          }))
      : [];
    const fileContextChips = this.contextOnly
      ? this.chips.filter(isFileChip)
      : [];
    const attachmentContextChips = this.contextOnly
      ? this.chips.filter(isAttachmentChip)
      : [];
    const tagContextChips = this.contextOnly
      ? this.chips.filter(isTagChip).map(chip => {
          const tag = this._tags.value.find(tag => tag.id === chip.tagId);
          return {
            chip,
            label: tag?.name ?? this.docDisplayConfig.getTagTitle(chip.tagId),
            meta: tag?.pageCount ? `${tag.pageCount} document(s)` : 'tag',
          };
        })
      : [];
    const collectionContextChips = this.contextOnly
      ? this.chips.filter(isCollectionChip).map(chip => {
          const collection = this._collections.value.find(
            collection => collection.id === chip.collectionId
          );
          const pageIds = this.docDisplayConfig.getCollectionPageIds(
            chip.collectionId
          );
          return {
            chip,
            label: collection?.name ?? chip.collectionId,
            meta: pageIds.length
              ? `${pageIds.length} document(s)`
              : 'collection',
          };
        })
      : [];
    const allChips = baseNonContextChips.concat(
      candidates.slice(0, MAX_CANDIDATES)
    );
    const isCollapsed = this.isCollapsed && allChips.length > 1;
    const nonContextChips = isCollapsed ? allChips.slice(0, 1) : allChips;
    const contextCount =
      docContextChips.length +
      fileContextChips.length +
      attachmentContextChips.length +
      tagContextChips.length +
      collectionContextChips.length +
      this.databaseAttachments.length +
      this.automationAttachments.length +
      this.workflowAttachments.length +
      this.codeSelections.length +
      this.mailAttachments.length +
      this.pdfContextAttachments.length +
      this.genericContextItems.length +
      selectedContextChips.length;
    const renderContextGroup = this.contextOnly && contextCount > 0;

    const contextChips = renderContextGroup
      ? html`<chat-panel-context-group-chip
          .docChips=${docContextChips}
          .fileChips=${fileContextChips}
          .attachmentChips=${attachmentContextChips}
          .tagChips=${tagContextChips}
          .collectionChips=${collectionContextChips}
          .databaseAttachments=${this.databaseAttachments}
          .automationAttachments=${this.automationAttachments}
          .workflowAttachments=${this.workflowAttachments}
          .codeSelections=${this.codeSelections}
          .mailAttachments=${this.mailAttachments}
          .pdfContextAttachments=${this.pdfContextAttachments}
          .genericContextItems=${this.genericContextItems}
          .selectedContextChips=${selectedContextChips}
          .removeDatabaseAttachment=${this.removeDatabaseAttachment}
          .removeAutomationAttachment=${this.removeAutomationAttachment}
          .removeWorkflowAttachment=${this.removeWorkflowAttachment}
          .removeCodeSelection=${this.removeCodeSelection}
          .removeMailAttachment=${this.removeMailAttachment}
          .removePdfContextAttachment=${this.removePdfContextAttachment}
          .removeChip=${this.removeChip}
          .removeCanvasSelectionItem=${this.removeCanvasSelectionItem}
          .removeCanvasSelectionItems=${this.removeCanvasSelectionItems}
          .portalContainer=${this.portalContainer}
        ></chat-panel-context-group-chip>`
      : html`
          ${docContextChips.map(({ chip }) => this._renderBaseChip(chip))}
          ${fileContextChips.map(chip => this._renderBaseChip(chip))}
          ${attachmentContextChips.map(chip => this._renderBaseChip(chip))}
          ${tagContextChips.map(({ chip }) => this._renderBaseChip(chip))}
          ${collectionContextChips.map(({ chip }) =>
            this._renderBaseChip(chip)
          )}
          ${repeat(
            this.databaseAttachments,
            db => db.blockId,
            db =>
              html`<chat-panel-database-chip
                .database=${db}
                .onRemove=${this.removeDatabaseAttachment}
              ></chat-panel-database-chip>`
          )}
          ${repeat(
            this.automationAttachments,
            a => a.blockId,
            a =>
              html`<chat-panel-automation-chip
                .automation=${a}
                .onRemove=${this.removeAutomationAttachment}
              ></chat-panel-automation-chip>`
          )}
          ${repeat(
            this.workflowAttachments,
            w => w.workflowId,
            w =>
              html`<chat-panel-workflow-chip
                .workflow=${w}
                .onRemove=${this.removeWorkflowAttachment}
              ></chat-panel-workflow-chip>`
          )}
          ${repeat(
            this.codeSelections,
            selection => selection.id,
            selection =>
              html`<chat-panel-code-selection-chip
                .codeSelection=${selection}
                .onRemove=${this.removeCodeSelection}
              ></chat-panel-code-selection-chip>`
          )}
          ${repeat(
            this.mailAttachments,
            mail => mail.id,
            mail =>
              html`<chat-panel-mail-chip
                .mail=${mail}
                .onRemove=${this.removeMailAttachment}
              ></chat-panel-mail-chip>`
          )}
          ${repeat(
            this.pdfContextAttachments,
            p => p.blockId,
            p =>
              html`<chat-panel-pdf-context-chip
                .pdf=${p}
                .onRemove=${this.removePdfContextAttachment}
              ></chat-panel-pdf-context-chip>`
          )}
          ${repeat(
            selectedContextChips,
            chip => getChipKey(chip),
            chip =>
              html`<chat-panel-selected-chip
                .chip=${chip}
                .removeChip=${this.removeChip}
                .removeCanvasSelectionItem=${this.removeCanvasSelectionItem}
                .removeCanvasSelectionItems=${this.removeCanvasSelectionItems}
                .portalContainer=${this.portalContainer}
              ></chat-panel-selected-chip>`
          )}
        `;

    if (this.contextOnly) {
      return contextCount
        ? html`<div class="ai-chat-panel-chips" data-context-only="true">
            ${contextChips}
          </div>`
        : nothing;
    }

    return html`<div class="ai-chat-panel-chips">
      ${contextChips}
      ${repeat(nonContextChips, chip => getChipKey(chip), this._renderBaseChip)}
      ${moreCandidates && !isCollapsed
        ? html`<div
            class="more-candidate-button"
            @click=${this._toggleMoreCandidatesMenu}
          >
            ${MoreVerticalIcon()}
          </div>`
        : nothing}
      ${isCollapsed
        ? html`<div class="collapse-button" @click=${this.toggleCollapse}>
            +${allChips.length - 1}
          </div>`
        : nothing}
    </div>`;
  }

  private readonly _renderBaseChip = (chip: ChatChip) => {
    if (isDocChip(chip)) {
      return html`<chat-panel-doc-chip
        .chip=${chip}
        .independentMode=${this.independentMode}
        .addChip=${this.addChip}
        .updateChip=${this.updateChip}
        .removeChip=${this.removeChip}
        .checkTokenLimit=${this._checkTokenLimit}
        .docDisplayConfig=${this.docDisplayConfig}
      ></chat-panel-doc-chip>`;
    }
    if (isFileChip(chip)) {
      return html`<chat-panel-file-chip
        .chip=${chip}
        .removeChip=${this.removeChip}
      ></chat-panel-file-chip>`;
    }
    if (isAttachmentChip(chip)) {
      return html`<chat-panel-attachment-chip
        .chip=${chip}
        .removeChip=${this.removeChip}
      ></chat-panel-attachment-chip>`;
    }
    if (isTagChip(chip)) {
      const tag = this._tags.value.find(tag => tag.id === chip.tagId);
      if (!tag) {
        return null;
      }
      return html`<chat-panel-tag-chip
        .chip=${chip}
        .tag=${tag}
        .removeChip=${this.removeChip}
      ></chat-panel-tag-chip>`;
    }
    if (isCollectionChip(chip)) {
      const collection = this._collections.value.find(
        collection => collection.id === chip.collectionId
      );
      if (!collection) {
        return null;
      }
      return html`<chat-panel-collection-chip
        .chip=${chip}
        .collection=${collection}
        .removeChip=${this.removeChip}
      ></chat-panel-collection-chip>`;
    }
    return null;
  };

  override connectedCallback(): void {
    super.connectedCallback();
    const tags = this.docDisplayConfig.getTags();
    this._tags = tags.signal;
    this._disposables.add(tags.cleanup);

    const collections = this.docDisplayConfig.getCollections();
    this._collections = collections.signal;
    this._disposables.add(collections.cleanup);
  }

  protected override updated(_changedProperties: PropertyValues): void {
    if (_changedProperties.has('chips')) {
      this._updateReferenceDocs();
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._cleanup?.();
  }

  private readonly _toggleMoreCandidatesMenu = () => {
    if (this._abortController) {
      this._abortController.abort();
      return;
    }

    this._abortController = new AbortController();
    this._abortController.signal.addEventListener('abort', () => {
      this._abortController = null;
    });

    const referenceDocs = computed(() =>
      this.referenceDocs.value.slice(MAX_CANDIDATES)
    );

    createLitPortal({
      template: html`
        <chat-panel-candidates-popover
          .addChip=${this.addChip}
          .referenceDocs=${referenceDocs}
          .docDisplayConfig=${this.docDisplayConfig}
          .abortController=${this._abortController}
        ></chat-panel-candidates-popover>
      `,
      portalStyles: {
        zIndex: 'var(--streamlain-z-index-popover)',
      },
      container: this.portalContainer ?? document.body,
      computePosition: {
        referenceElement: this.moreCandidateButton,
        placement: 'top-start',
        middleware: [offset({ crossAxis: 0, mainAxis: 8 }), flip()],
        autoUpdate: { animationFrame: true },
      },
      abortController: this._abortController,
      closeOnClickAway: true,
    });
  };

  private readonly _checkTokenLimit = (
    newChip: DocChip,
    newTokenCount: number
  ) => {
    const estimatedTokens = this.chips.reduce((acc, chip) => {
      if (isFileChip(chip) || isTagChip(chip) || isCollectionChip(chip)) {
        return acc;
      }
      if (isDocChip(chip) && chip.docId === newChip.docId) {
        return acc + newTokenCount;
      }

      if (
        isDocChip(chip) &&
        chip.markdown?.value &&
        chip.state === 'finished'
      ) {
        const tokenCount =
          chip.tokenCount ?? estimateTokenCount(chip.markdown.value);
        return acc + tokenCount;
      }
      if (isSelectedContextChip(chip)) {
        const tokenCount =
          estimateTokenCount(chip.combinedElementsMarkdown ?? '') +
          estimateTokenCount(chip.snapshot ?? '') +
          estimateTokenCount(chip.html ?? '');
        return acc + tokenCount;
      }
      return acc;
    }, 0);
    return estimatedTokens <= MAX_TOKEN_COUNT;
  };

  private readonly _updateReferenceDocs = () => {
    const docIds = this.chips
      .filter(isDocChip)
      .filter(chip => chip.state !== 'candidate')
      .map(chip => chip.docId);
    if (isEqual(this._docIds, docIds)) {
      return;
    }

    this._cleanup?.();
    this._docIds = docIds;
    const { signal, cleanup } = this.docDisplayConfig.getReferenceDocs(docIds);
    this.referenceDocs = signal;
    this._cleanup = cleanup;
  };
}
